import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../config";

export const LANGS = ["es", "en"];
export const DEFAULT_LANG = "es";

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
});

const detectInitialLang = () => {
  try {
    const u = new URLSearchParams(window.location.search).get("lang");
    if (u && LANGS.includes(u)) return u;
    const ls = localStorage.getItem("site_lang");
    if (ls && LANGS.includes(ls)) return ls;
    const tl = localStorage.getItem("travel_lang");
    if (tl && LANGS.includes(tl)) return tl;
    // Quick browser hint while waiting for the geo API
    const nav = (navigator.language || "es").toLowerCase();
    if (nav.startsWith("en")) return "en";
  } catch {}
  return DEFAULT_LANG;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitialLang);

  const setLang = useCallback((next) => {
    if (!LANGS.includes(next)) return;
    setLangState(next);
    try {
      localStorage.setItem("site_lang", next);
      localStorage.setItem("travel_lang", next);
      // Mark as user-chosen so the geo detector never overrides it again
      localStorage.setItem("site_lang_source", "user");
    } catch {}
  }, []);

  useEffect(() => {
    try {
      document.documentElement.setAttribute("lang", lang);
    } catch {}
  }, [lang]);

  // First-visit-only IP geolocation detection. Only runs if:
  //   - the user has NOT explicitly picked a language (site_lang_source != "user"), and
  //   - there is no ?lang= override in the URL.
  // Runs once per mount; the backend uses ipapi.co with a Canary-Islands-friendly default.
  useEffect(() => {
    try {
      const userChosen = localStorage.getItem("site_lang_source") === "user";
      const urlLang = new URLSearchParams(window.location.search).get("lang");
      if (userChosen || urlLang) return;
    } catch { return; }

    let cancelled = false;
    axios.get(`${API}/i18n/detect`, { timeout: 5000 })
      .then((r) => {
        if (cancelled) return;
        const detected = r?.data?.lang;
        if (detected && LANGS.includes(detected) && detected !== lang) {
          setLangState(detected);
          try {
            localStorage.setItem("site_lang", detected);
            localStorage.setItem("travel_lang", detected);
            // Do NOT mark as "user" so a click on the switcher still wins
            localStorage.setItem("site_lang_source", "geo");
          } catch {}
        }
      })
      .catch(() => { /* fail silently — keep current lang */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
